import React, { createContext, useContext } from 'react';
import classNames from 'classnames';
import styles from './Stepper.module.css';

const StepperContext = createContext(null);

function StepperContainer({ value, className, children, ...rest }) {
  return (
    <StepperContext.Provider value={value}>
      <div className={classNames(styles.root, className)} {...rest}>
        {children}
      </div>
    </StepperContext.Provider>
  );
}

function StepperStep({ value, className, children, ...rest }) {
  const currentValue = useContext(StepperContext);

  return (
    <div
      role="listitem"
      id={getId(value)}
      className={classNames(styles.step, { [styles.stepActive]: value === currentValue }, className)}
      {...rest}
    >
      {children}
    </div>
  );
}

function StepperList({ className, children, ...rest }) {
  return (
    <div role="list" className={classNames(styles.list, className)} {...rest}>
      {children}
    </div>
  );
}

function StepperPanel({ value, className, children, ...rest }) {
  const currentValue = useContext(StepperContext);

  if (currentValue !== value) {
    return null;
  }

  return (
    <div aria-labelledby={getId(value)} className={classNames(styles.panel, className)} {...rest}>
      {children}
    </div>
  );
}

function Stepper(props) {
  const { value, steps, containerComponent: Container = StepperContainer, listComponent: List = StepperList } = props;

  return (
    <Container value={value}>
      <List>{steps.map((step) => renderStep(props, step))}</List>
      {steps.map((step) => renderPanel(props, step))}
    </Container>
  );
}

Stepper.Container = StepperContainer;

Stepper.Step = StepperStep;

Stepper.List = StepperList;

Stepper.Panel = StepperPanel;

export default Stepper;

function renderStep({ stepComponent: Step = StepperStep, getStepProps }, step) {
  const stepProps = typeof getStepProps === 'function' ? getStepProps(step) : getDefaultStepProps(step);

  return (
    <Step key={step.value} {...stepProps}>
      {renderContent(step.renderStep, step, step.label)}
    </Step>
  );
}

function renderPanel({ panelComponent: Panel = StepperPanel, getPanelProps }, step) {
  const panelProps = typeof getPanelProps === 'function' ? getPanelProps(step) : getDefaultPanelProps(step);

  return (
    <Panel key={step.value} {...panelProps}>
      {renderContent(step.renderPanel ?? step.render, step, null)}
    </Panel>
  );
}

function renderContent(render, props, children) {
  if (typeof render === 'function') {
    return render(props);
  }

  return children;
}

function getDefaultStepProps({ value }) {
  return { value };
}

function getDefaultPanelProps({ value }) {
  return { value };
}

function getId(value) {
  return `step_${value}`;
}
